import { Injectable, NgZone } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { ConsentProvider, CookieCategory } from './consent-provider';

@Injectable({
  providedIn: 'root',
})
export class CookieYesConsentService implements ConsentProvider {
  private consentChanged$ = new Subject<void>();

  constructor(private zone: NgZone) {
    this.update();

    // React to CookieYes preference updates (event names vary by version)
    if (typeof window !== 'undefined') {
      window.addEventListener('cookieyes_preferences_update', () => this.zone.run(() => this.update()));
      window.addEventListener('cookieyes_consent_update', () => this.zone.run(() => this.update()));
    }
  }

  hasConsentFor(category: CookieCategory): boolean {
    // Strictly necessary is always allowed
    if (category === CookieCategory.StrictlyNecessary) return true;

    const cookieYesCategories = this.mapCategory(category);
    return cookieYesCategories.some((c) => this.cookieYesAllowed(c));
  }

  consentChanges(): Observable<void> {
    return this.consentChanged$.asObservable();
  }

  private update(): void {
    this.consentChanged$.next();
  }

  private cookieYesAllowed(key: CookieYesKey): boolean {
    const raw = this.getCookie('cookieyes-consent');
    if (!raw) return false;

    const decoded = decodeURIComponent(raw);

    // Try JSON first
    try {
      // CookieYes sometimes stores JSON with category booleans
      const obj = JSON.parse(decoded) as Record<string, unknown>;
      const v = obj[key];
      return this.toBoolean(v);
    } catch {
      // Fallback: parse key/value pairs like "necessary:true; functional:true; analytics:false"
      const pairs = decoded
        .split(/[;&]/)
        .map((p) => p.trim())
        .filter(Boolean);
      for (const p of pairs) {
        const [k, v] = p.split(/[:=]/).map((s) => s.trim().toLowerCase());
        if (!k) continue;
        if (k === key) return this.toBoolean(v);
        // Some setups use synonyms: "advertisement" or "ad" for targeting/ads
        if (key === 'advertisement' && (k === 'ads' || k === 'ad' || k === 'advertising' || k === 'targeting')) {
          return this.toBoolean(v);
        }
        if (key === 'performance' && k === 'analytics') {
          return this.toBoolean(v);
        }
      }
      return false;
    }
  }

  private toBoolean(v: unknown): boolean {
    if (typeof v === 'boolean') return v;
    if (typeof v === 'string') {
      const s = v.toLowerCase();
      return s === 'true' || s === 'yes' || s === '1';
    }
    if (typeof v === 'number') return v === 1;
    return false;
  }

  private getCookie(name: string): string | null {
    if (typeof document === 'undefined') return null;
    const match = document.cookie.match(new RegExp('(^|;\\s*)' + name + '=([^;]+)'));
    return match ? match[2] : null;
  }

  private mapCategory(category: CookieCategory): CookieYesKey[] {
    switch (category) {
      case CookieCategory.Performance:
        // CookieYes often uses 'performance' or 'analytics' for this bucket
        return ['performance', 'analytics'];
      case CookieCategory.Functionality:
        return ['functional'];
      case CookieCategory.TargetingAdvertising:
        // CookieYes commonly uses 'advertisement' or 'targeting'
        return ['advertisement'];
      case CookieCategory.StrictlyNecessary:
      default:
        return ['necessary'];
    }
  }
}

type CookieYesKey = 'necessary' | 'performance' | 'functional' | 'analytics' | 'advertisement';
